import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { ToastService } from '../../services/toast.service';
import { LoggerService } from '../../services/logger.service';
import { LanguageService } from '../../services/language.service';
import { getSessionMessage } from './session-messages';

export interface SessionTimeoutConfig {
  /** Inactivity duration before logout (in milliseconds) */
  timeoutDuration: number;
  /** Duration before timeout to display a warning (in milliseconds) */
  warningDuration?: number;
  /** DOM events to monitor for user activity detection */
  activityEvents: string[];
}

/**
 * User session timeout management service
 *
 * Automatically logs out the user after a defined period of inactivity.
 * Monitors user interactions (clicks, keystrokes, mouse movements, scroll, etc.)
 * and resets the timer with each detected activity.
 *
 * Acceptance Criteria:
 * - AC1: Session expires automatically after 30 minutes of inactivity
 * - AC2: User is redirected to login screen with an expiration message
 * - AC4: System prevents unauthorized access after expiration
 */
@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly logger = inject(LoggerService);
  private readonly ngZone = inject(NgZone);
  private readonly languageService = inject(LanguageService);

  // Default configuration: 30 minutes of inactivity
  private readonly DEFAULT_CONFIG: SessionTimeoutConfig = {
    timeoutDuration: 30 * 60 * 1000, // 30 minutes in milliseconds
    warningDuration: 5 * 60 * 1000, // Warning 5 minutes before
    activityEvents: ['click', 'keypress', 'scroll', 'mousemove', 'touchstart', 'wheel'],
  };

  private config: SessionTimeoutConfig = this.DEFAULT_CONFIG;
  private timeoutId?: number;
  private warningTimeoutId?: number;
  private lastActivityTime: number = Date.now();
  private isMonitoring = false;
  private eventListeners: Array<() => void> = [];

  /**
   * Initializes session monitoring with optional configuration
   */
  initialize(customConfig?: Partial<SessionTimeoutConfig>): void {
    if (this.isMonitoring) {
      this.logger.warn('Session timeout monitoring already initialized');
      return;
    }

    this.config = { ...this.DEFAULT_CONFIG, ...customConfig };

    this.logger.info('Initializing session timeout monitoring', {
      timeoutMinutes: this.config.timeoutDuration / (60 * 1000),
      language: this.languageService.getCurrentLanguage(),
    }); // Start monitoring only if user is authenticated
    if (this.authService.isAuthenticated()) {
      this.startMonitoring();
    }

    // Observe authentication state changes
    this.authService.authState$.subscribe((state) => {
      if (state.isAuthenticated && !this.isMonitoring) {
        this.startMonitoring();
      } else if (!state.isAuthenticated && this.isMonitoring) {
        this.stopMonitoring();
      }
    });
  }

  /**
   * Starts user activity monitoring
   */
  private startMonitoring(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    this.lastActivityTime = Date.now();

    this.logger.info('Session timeout monitoring started');

    // Register event listeners to detect user activity
    this.registerActivityListeners();

    // Start the timeout timer
    this.resetTimeout();
  }

  /**
   * Stops activity monitoring
   */
  private stopMonitoring(): void {
    if (!this.isMonitoring) {
      return;
    }

    this.isMonitoring = false;
    this.clearTimeouts();
    this.removeActivityListeners();

    this.logger.info('Session timeout monitoring stopped');
  }

  /**
   * Registers DOM event listeners to detect activity
   */
  private registerActivityListeners(): void {
    // Use NgZone.runOutsideAngular to avoid triggering change detection
    // on every user event (performance optimization)
    this.ngZone.runOutsideAngular(() => {
      this.config.activityEvents.forEach((eventType) => {
        const handler = () => this.onUserActivity();

        document.addEventListener(eventType, handler, { passive: true });

        // Save for later cleanup
        this.eventListeners.push(() => {
          document.removeEventListener(eventType, handler);
        });
      });
    });
  }

  /**
   * Removes all event listeners
   */
  private removeActivityListeners(): void {
    this.eventListeners.forEach((removeListener) => removeListener());
    this.eventListeners = [];
    this.logger.debug('Activity listeners removed');
  }

  /**
   * Callback called when user activity is detected
   */
  private onUserActivity(): void {
    if (!this.isMonitoring) {
      return;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;

    // Throttle: only reset the timer if last activity
    // was more than 1 second ago (avoids too many calls)
    if (timeSinceLastActivity > 1000) {
      this.lastActivityTime = now;
      this.resetTimeout();
    }
  }

  /**
   * Resets the timeout and warning timers
   */
  private resetTimeout(): void {
    this.clearTimeouts();

    // Timer for warning (optional)
    if (this.config.warningDuration && this.config.warningDuration > 0) {
      const warningTime = this.config.timeoutDuration - this.config.warningDuration;

      this.warningTimeoutId = window.setTimeout(() => {
        this.onWarningTimeout();
      }, warningTime);
    }

    // Timer for automatic logout
    this.timeoutId = window.setTimeout(() => {
      this.onSessionTimeout();
    }, this.config.timeoutDuration);
  }

  /**
   * Called when the warning time is reached
   */
  private onWarningTimeout(): void {
    this.ngZone.run(() => {
      const remainingMinutes = Math.round((this.config.warningDuration || 0) / (60 * 1000));

      const currentLang = this.languageService.getCurrentLanguage() as 'fr' | 'en';
      const message = getSessionMessage('sessionWarning', currentLang, remainingMinutes);
      this.toastService.showWarning(message, 10000);

      this.logger.info('Session timeout warning displayed', { remainingMinutes });
    });
  }

  /**
   * Called when the session expires
   * AC1: Session expires automatically after 30 minutes of inactivity
   * AC2: User is redirected to login screen with a message
   * AC4: System prevents unauthorized access after expiration
   */
  private onSessionTimeout(): void {
    this.ngZone.run(() => {
      this.logger.info('Session timeout - logging out user due to inactivity');

      // Stop monitoring
      this.stopMonitoring();

      // Log out the user
      this.authService.logout('inactivity');

      // Display an expiration message
      const currentLang = this.languageService.getCurrentLanguage() as 'fr' | 'en';
      const message = getSessionMessage('sessionExpired', currentLang);
      this.toastService.showWarning(message, 8000);

      // Redirect to login page with an expiration parameter
      this.router.navigate(['/login'], {
        queryParams: { sessionExpired: 'true' },
      });
    });
  }

  /**
   * Clears all active timers
   */
  private clearTimeouts(): void {
    if (this.timeoutId !== undefined) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }

    if (this.warningTimeoutId !== undefined) {
      window.clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = undefined;
    }
  }

  /**
   * Manually resets the timer (useful for HTTP requests)
   */
  resetActivityTimer(): void {
    if (this.isMonitoring) {
      this.lastActivityTime = Date.now();
      this.resetTimeout();
    }
  }

  /**
   * Gets the remaining time before expiration (in milliseconds)
   */
  getTimeUntilTimeout(): number {
    if (!this.isMonitoring) {
      return 0;
    }

    const elapsed = Date.now() - this.lastActivityTime;
    const remaining = this.config.timeoutDuration - elapsed;

    return Math.max(0, remaining);
  }

  /**
   * Checks if a session is active
   */
  isSessionActive(): boolean {
    return this.isMonitoring && this.getTimeUntilTimeout() > 0;
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.stopMonitoring();
  }
}
